import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WAIVER_HTML = `
<h3>Care Anywhere — Liability Waiver &amp; Care Authorization</h3>
<p>By signing below I, the parent or legal guardian, acknowledge:</p>
<ol>
  <li><strong>Care Authorization.</strong> I authorize Care Anywhere personnel (background-checked, CPR-certified caregivers) to provide supervision, comfort, feeding, hygiene assistance, and age-appropriate play activities for my child(ren) at the scheduled event or engagement.</li>
  <li><strong>Health &amp; Allergies.</strong> I have disclosed all known allergies, medical conditions, dietary restrictions, and behavioral considerations. I authorize Care Anywhere to administer basic first aid and, in the event of an emergency, to contact emergency services and transport my child to the nearest hospital.</li>
  <li><strong>Assumption of Risk.</strong> I understand that ordinary risks are inherent in group childcare and event environments. I release Care Anywhere, its staff, contractors, and Elias Academia from liability for injuries or losses that occur despite the reasonable care of the personnel.</li>
  <li><strong>Photography.</strong> Care Anywhere does not photograph clients or their children unless separate written consent is provided.</li>
  <li><strong>Age &amp; Custody.</strong> I confirm I am the legal parent/guardian of the listed child(ren) and have the authority to consent to this care.</li>
  <li><strong>Governing Law.</strong> This waiver is governed by the laws of the State of California.</li>
</ol>
<p>By checking the acknowledgement box below, I agree to the terms above and confirm that the information I have provided is accurate.</p>
`;

const WaiverConsentModal = ({ parentToken, onDone, onClose }) => {
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [marketing, setMarketing] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!waiverAccepted) {
      toast.error("Please acknowledge the waiver to continue.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/portal/parent/consent`,
        { waiver_accepted: true, marketing_consent: !!marketing },
        { headers: { Authorization: `Bearer ${parentToken}` } },
      );
      toast.success("Waiver accepted — thank you.");
      onDone?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save your acknowledgement.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="waiver-modal" className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-2xl max-h-[90vh] bg-background rounded-2xl border border-border shadow-2xl overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-primary" />
            <div className="font-serif-display text-lg">Care authorization &amp; waiver</div>
          </div>
          {onClose && (
            <button onClick={onClose} data-testid="waiver-close" className="rounded-full h-8 w-8 hover:bg-muted flex items-center justify-center">
              <X size={16} />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 text-sm text-foreground/90 leading-relaxed prose-sm max-w-none">
          <div dangerouslySetInnerHTML={{ __html: WAIVER_HTML }} className="[&_h3]:font-serif-display [&_h3]:text-xl [&_h3]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol_li]:mb-2" />
        </div>

        <div className="border-t border-border px-6 py-4 space-y-3">
          <label className="flex items-start gap-2.5 cursor-pointer">
            <Checkbox data-testid="waiver-accept" checked={waiverAccepted} onCheckedChange={(v) => setWaiverAccepted(!!v)} className="mt-0.5" />
            <span className="text-sm text-foreground leading-relaxed">
              I have read and agree to the Care Anywhere Waiver &amp; Care Authorization above.
            </span>
          </label>
          <label className="flex items-start gap-2.5 cursor-pointer">
            <Checkbox data-testid="marketing-consent" checked={marketing} onCheckedChange={(v) => setMarketing(!!v)} className="mt-0.5" />
            <span className="text-xs text-muted-foreground leading-relaxed">
              I'd like to receive service updates, availability notices, and occasional promotions from Care Anywhere. I can unsubscribe any time.
            </span>
          </label>
          <div className="flex justify-end gap-2 pt-1">
            {onClose && (
              <Button variant="ghost" onClick={onClose} className="rounded-full">Skip for now</Button>
            )}
            <Button data-testid="waiver-submit" disabled={busy || !waiverAccepted} onClick={submit} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              {busy ? "Saving…" : "Accept & continue"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaiverConsentModal;
